import { FC, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import axios from 'axios'
import env from '@consts/consts'
import { useSetAtom } from 'jotai'
import { accessTokenAtom, refreshTokenAtom, userAtom } from '@services/token/token.models'
import { storeTokens } from '@services/token/tokenStorage'

const AuthCallback: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()
  const setAccessToken = useSetAtom(accessTokenAtom)
  const setRefreshToken = useSetAtom(refreshTokenAtom)
  const setUser = useSetAtom(userAtom)
  const hasProcessed = useRef(false)

  useEffect(() => {
    if (hasProcessed.current) {
      return
    }

    const handleCallback = async () => {
      hasProcessed.current = true
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')
      const storedState = sessionStorage.getItem('oauth_state')

      if (error) {
        enqueueSnackbar(
          formatMessage({
            id: 'auth.microsoft.error',
            defaultMessage: 'Błąd autoryzacji Microsoft'
          }),
          { variant: 'error' }
        )
        navigate('/')
        return
      }

      if (!code || !state) {
        enqueueSnackbar(
          formatMessage({
            id: 'auth.microsoft.invalid',
            defaultMessage: 'Nieprawidłowa odpowiedź autoryzacji'
          }),
          { variant: 'error' }
        )
        navigate('/')
        return
      }

      if (state !== storedState) {
        enqueueSnackbar(
          formatMessage({
            id: 'auth.microsoft.csrf',
            defaultMessage: 'Błąd weryfikacji bezpieczeństwa'
          }),
          { variant: 'error' }
        )
        navigate('/')
        return
      }

      sessionStorage.removeItem('oauth_state')

      try {
        const response = await axios.post(`${env.API_BASE_URL}/admin/accounts/microsoft/token/`, {
          code: code,
          redirect_uri: env.REDIRECT_URI
        })

        const { access, refresh, user_roles, user_data, email, user } = response.data

        console.log('Microsoft OAuth response:', {
          user_data,
          email,
          user,
          user_roles,
          full_response: response.data
        })

        // Spróbuj wyciągnąć email z różnych źródeł
        let userEmail = 'user'
        let userData = user_data || {}
        let roles = user_roles || []

        // Backend zwraca dane w obiekcie 'user'
        if (user && typeof user === 'object') {
          userEmail = user.email || user.username || 'user'
          userData = {
            email: user.email,
            name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            first_name: user.first_name,
            last_name: user.last_name,
            id: user.id
          }
          // Możemy próbować wykryć rolę na podstawie innych danych
          // lub użyć domyślnej roli
        } else if (email) {
          userEmail = email
        } else if (user && typeof user === 'string') {
          userEmail = user
        } else if (user_data?.email) {
          userEmail = user_data.email
        } else {
          // Jeśli nie, spróbuj zdekodować z JWT
          try {
            const tokenParts = access.split('.')
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]))
              console.log('JWT payload:', payload)
              userEmail =
                payload.email ||
                payload.preferred_username ||
                payload.unique_name ||
                payload.upn ||
                payload.user_id ||
                'user'

              // Spróbuj wyciągnąć role z JWT
              if (payload.roles) {
                roles = payload.roles
                console.log('Roles from JWT:', roles)
              }

              // Utwórz user_data z payload JWT
              userData = {
                email: userEmail,
                name: payload.name || payload.given_name || '',
                ...payload
              }
            }
          } catch (e) {
            console.error('Failed to decode JWT:', e)
          }
        }

        // Zapisz tokeny spójnie we wszystkich formatach (klasyczny + MS/AD)
        storeTokens({ access, refresh })

        // Zapisz role tylko jeśli są dostępne, w przeciwnym razie usuń z localStorage
        if (roles && roles.length > 0) {
          localStorage.setItem('user_roles', JSON.stringify(roles))
        } else {
          localStorage.removeItem('user_roles')
        }

        localStorage.setItem('user_data', JSON.stringify(userData))

        // Następnie ustaw atomy Jotai (bez duplikowania zapisu w localStorage)
        setAccessToken(access)
        setRefreshToken(refresh)
        setUser(userEmail)

        localStorage.setItem('user', JSON.stringify(userEmail))

        // Jeśli nie mamy danych użytkownika, spróbuj pobrać je z backendu
        if (userEmail === 'user' || !userData.email) {
          try {
            // Spróbuj pobrać dane użytkownika używając nowego tokenu
            const verifyResponse = await axios.get(`${env.API_BASE_URL}/admin/auth/verify/`, {
              headers: {
                Authorization: `Bearer ${access}`
              }
            })
            console.log('User verify response:', verifyResponse.data)

            // Jeśli backend zwraca dane użytkownika
            if (verifyResponse.data?.user || verifyResponse.data?.email) {
              const verifiedEmail =
                verifyResponse.data.email || verifyResponse.data.user || userEmail
              localStorage.setItem('user', JSON.stringify(verifiedEmail))
              setUser(verifiedEmail)

              if (verifyResponse.data.user_data) {
                localStorage.setItem('user_data', JSON.stringify(verifyResponse.data.user_data))
              }
            }
          } catch (verifyError) {
            console.log('Could not verify user:', verifyError)
          }
        }

        // Poczekaj chwilę na aktualizację stanu
        await new Promise((resolve) => setTimeout(resolve, 100))

        enqueueSnackbar(
          formatMessage({
            id: 'auth.microsoft.success',
            defaultMessage: 'Zalogowano pomyślnie'
          }),
          { variant: 'success' }
        )

        const isTechnician =
          roles &&
          roles.length > 0 &&
          roles.some((role: string) =>
            ['technician', 'technicians', 'administrator', 'administrators'].includes(role)
          )

        // Dodaj małe opóźnienie przed nawigacją
        setTimeout(() => {
          navigate(isTechnician ? '/helpdesk/tickets' : '/', { replace: true })
        }, 100)
      } catch (error) {
        console.error('Microsoft auth error:', error)
        enqueueSnackbar(
          formatMessage({
            id: 'auth.microsoft.failed',
            defaultMessage: 'Nie udało się zalogować przez Microsoft'
          }),
          { variant: 'error' }
        )
        navigate('/')
      }
    }

    handleCallback()
  }, []) // Usunięte zależności aby zapobiec wielokrotnemu wywołaniu

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2
      }}
    >
      <CircularProgress />
      <Typography>
        {formatMessage({
          id: 'auth.microsoft.processing',
          defaultMessage: 'Autoryzacja...'
        })}
      </Typography>
    </Box>
  )
}

export default AuthCallback
